'use client'

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FaGoogle } from 'react-icons/fa'
import {
    MdCheckCircle,
    MdContentCopy,
    MdInfoOutline,
    MdLink,
    MdLinkOff,
    MdRefresh,
    MdVpnKey,
    MdWarning,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import StoreGoogleTagsCard from './store-google-tags-card'
import {
    disconnectGoogleAds,
    getGoogleAdsConnection,
    getGoogleAdsCustomers,
    getGoogleAdsLeadForm,
    getGoogleAdsOAuthUrl,
    GoogleAdsConnectionSummary,
    GoogleAdsCustomersResult,
    GoogleAdsLeadFormSummary,
    rotateGoogleAdsLeadFormKey,
    updateGoogleAdsSelection,
} from '@/lib/api/marketing/google-ads-api'

const STATUS_BADGE: Record<string, { label: string; class: string }> = {
    ACTIVE: { label: 'Conectado', class: 'badge-success' },
    PENDING: { label: 'Pendente', class: 'badge-warning' },
    EXPIRED: { label: 'Acesso expirado', class: 'badge-error' },
    REVOKED: { label: 'Desconectado', class: 'badge-ghost' },
}

/** `1234567890` → `123-456-7890`, como o Google Ads mostra. */
function formatCustomerId(id: string | null | undefined): string {
    const d = (id ?? '').replace(/\D/g, '')
    return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : id ?? ''
}

/**
 * Conexão da organização com o Google Ads. Base da integração: a conta fica ligada
 * agora; conversões offline (cobrança paga) e custo por campanha entram quando a
 * plataforma tiver o acesso Básico aprovado pelo Google.
 */
export default function GoogleAdsIntegrationPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [connection, setConnection] = useState<GoogleAdsConnectionSummary | null>(null)
    const [customers, setCustomers] = useState<GoogleAdsCustomersResult | null>(null)
    const [customerId, setCustomerId] = useState('')
    const [loginCustomerId, setLoginCustomerId] = useState('')
    const [loading, setLoading] = useState(true)
    const [connecting, setConnecting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [showDisconnect, setShowDisconnect] = useState(false)
    const [disconnecting, setDisconnecting] = useState(false)
    const [leadForm, setLeadForm] = useState<GoogleAdsLeadFormSummary | null>(null)
    const [rotatingKey, setRotatingKey] = useState(false)
    const [showRotateKey, setShowRotateKey] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            // O formulário de lead não depende da conexão: carrega sempre.
            getGoogleAdsLeadForm().then(form => {
                if (form.success) setLeadForm(form.result)
            })

            const res = await getGoogleAdsConnection()
            if (res.success) {
                setConnection(res.result)
                setCustomerId(formatCustomerId(res.result.customerId))
                setLoginCustomerId(formatCustomerId(res.result.loginCustomerId))
                if (res.result.status === 'ACTIVE') {
                    const list = await getGoogleAdsCustomers()
                    if (list.success) setCustomers(list.result)
                }
            }
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    // Retorno do OAuth (?google_ads_connected=1|0)
    useEffect(() => {
        const connected = searchParams.get('google_ads_connected')
        if (connected === '1') {
            toast.success('Conta do Google Ads conectada')
        } else if (connected === '0') {
            toast.error(`Falha ao conectar o Google Ads (${searchParams.get('error') || 'desconhecido'})`)
        }
        if (connected) {
            searchParams.delete('google_ads_connected')
            searchParams.delete('error')
            setSearchParams(searchParams, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleConnect = async () => {
        setConnecting(true)
        try {
            const res = await getGoogleAdsOAuthUrl()
            if (res.success && res.result?.url) {
                window.location.href = res.result.url
                return
            }
            toast.error(res.error || 'Não foi possível iniciar a conexão com o Google')
        } catch {
            toast.error('Erro ao iniciar a conexão com o Google')
        }
        setConnecting(false)
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const res = await updateGoogleAdsSelection({
                customerId: customerId || null,
                loginCustomerId: loginCustomerId || null,
            })
            if (res.success) {
                setConnection(res.result)
                toast.success('Conta do Google Ads salva')
            } else {
                toast.error(res.error || 'Falha ao salvar a conta')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleDisconnect = async () => {
        setDisconnecting(true)
        try {
            const res = await disconnectGoogleAds()
            if (res.success) {
                toast.success('Google Ads desconectado')
                setShowDisconnect(false)
                setCustomers(null)
                await load()
            } else {
                toast.error(res.error || 'Falha ao desconectar')
            }
        } finally {
            setDisconnecting(false)
        }
    }

    const handleRotateKey = async () => {
        setRotatingKey(true)
        try {
            const res = await rotateGoogleAdsLeadFormKey()
            if (res.success) {
                setLeadForm(res.result)
                setShowRotateKey(false)
                toast.success(
                    leadForm?.key
                        ? 'Nova chave gerada. Atualize-a no formulário do Google Ads.'
                        : 'Chave gerada'
                )
            } else {
                toast.error(res.error || 'Falha ao gerar a chave')
            }
        } finally {
            setRotatingKey(false)
        }
    }

    const copyToClipboard = async (value: string, label: string) => {
        try {
            await navigator.clipboard.writeText(value)
            toast.success(`${label} copiada`)
        } catch {
            toast.error('Não foi possível copiar. Selecione o texto e copie manualmente.')
        }
    }

    const isActive = connection?.status === 'ACTIVE'
    const badge = connection?.status ? STATUS_BADGE[connection.status] : null

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <FaGoogle className="text-2xl text-secondary" />
                </div>
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-base-content">Integração Google Ads</h1>
                    <p className="text-sm text-base-content/60">
                        Conecte a conta do Google Ads para atribuir os leads aos anúncios e medir o retorno por campanha.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : !isActive ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-10 md:p-16 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary/10 flex items-center justify-center">
                        <MdLink className="text-4xl text-secondary/50" />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-base-content mb-2">
                        {connection?.status === 'EXPIRED' ? 'Acesso expirado' : 'Nenhuma conta do Google Ads conectada'}
                    </h3>
                    <p className="text-sm text-base-content/60 mb-6 max-w-md mx-auto">
                        Entre com a conta Google que administra os anúncios. Nenhuma senha fica guardada no sistema.
                    </p>
                    {connection?.lastError && (
                        <p className="text-xs text-error/70 mb-4">Último erro: {connection.lastError}</p>
                    )}
                    <button className="btn btn-secondary gap-2" onClick={handleConnect} disabled={connecting}>
                        {connecting ? <span className="loading loading-spinner loading-sm" /> : <FaGoogle />}
                        {connection?.status === 'EXPIRED' ? 'Reconectar Google Ads' : 'Conectar Google Ads'}
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <MdCheckCircle className="text-3xl text-success" />
                                <div>
                                    <p className="font-semibold text-base-content">Conta Google conectada</p>
                                    <p className="text-xs text-base-content/60">{connection?.googleEmail || '-'}</p>
                                </div>
                            </div>
                            {badge && <span className={`badge ${badge.class}`}>{badge.label}</span>}
                        </div>
                    </div>

                    {customers && !customers.ready && (
                        <div className={`alert ${customers.awaitingApproval ? '' : 'alert-warning'}`}>
                            {customers.awaitingApproval ? (
                                <MdInfoOutline className="text-xl" />
                            ) : (
                                <MdWarning className="text-xl" />
                            )}
                            <span className="text-sm">
                                {customers.reason ||
                                    'A plataforma aguarda a aprovação do Google para a API do Google Ads.'}
                                {customers.awaitingApproval &&
                                    ' Informe a conta abaixo; a lista de contas, as conversões e o custo por campanha passam a funcionar assim que a aprovação sair, sem reconectar.'}
                            </span>
                        </div>
                    )}

                    <div className="bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-5">
                        <h3 className="font-semibold text-base-content">Conta do Google Ads</h3>

                        {customers?.ready && customers.customers.length > 0 && (
                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-sm font-medium">Contas disponíveis</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={customerId.replace(/\D/g, '')}
                                    onChange={e => setCustomerId(formatCustomerId(e.target.value))}
                                >
                                    <option value="">Selecione…</option>
                                    {customers.customers
                                        .filter(c => !c.isManager)
                                        .map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name ? `${c.name} (${formatCustomerId(c.id)})` : formatCustomerId(c.id)}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-sm font-medium">ID da conta do Google Ads</span>
                                </label>
                                <input
                                    className="input input-bordered w-full"
                                    placeholder="123-456-7890"
                                    value={customerId}
                                    onChange={e => setCustomerId(e.target.value)}
                                />
                                <span className="label-text-alt text-base-content/50 mt-1">
                                    Aparece no canto superior do Google Ads.
                                </span>
                            </div>
                            <div className="form-control w-full">
                                <label className="label py-1">
                                    <span className="label-text text-sm font-medium">Conta administradora (opcional)</span>
                                </label>
                                <input
                                    className="input input-bordered w-full"
                                    placeholder="123-456-7890"
                                    value={loginCustomerId}
                                    onChange={e => setLoginCustomerId(e.target.value)}
                                />
                                <span className="label-text-alt text-base-content/50 mt-1">
                                    Só se o acesso à conta for por uma conta de administrador (MCC).
                                </span>
                            </div>
                        </div>

                        {connection?.customerName && (
                            <p className="text-sm text-base-content/70">
                                Conta salva: <strong>{connection.customerName}</strong>
                            </p>
                        )}

                        <div className="flex justify-end">
                            <button className="btn btn-secondary btn-sm" onClick={handleSave} disabled={saving}>
                                {saving && <span className="loading loading-spinner loading-xs" />}
                                Salvar conta
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                        <button className="btn btn-ghost btn-sm gap-1" onClick={load} disabled={saving}>
                            <MdRefresh className="text-lg" /> Atualizar
                        </button>
                        <button className="btn btn-ghost btn-sm text-error gap-1" onClick={() => setShowDisconnect(true)}>
                            <MdLinkOff className="text-lg" /> Desconectar
                        </button>
                    </div>

                    {connection?.lastError && (
                        <div className="alert alert-warning">
                            <MdWarning />
                            <span className="text-sm">{connection.lastError}</span>
                        </div>
                    )}
                </div>
            )}

            {!loading && (
                <div className="mt-6 bg-base-100 rounded-2xl border border-base-300/50 shadow-sm p-6 space-y-5">
                    <div>
                        <h3 className="font-semibold text-base-content">Formulário de lead do Google</h3>
                        <p className="text-sm text-base-content/60 mt-1">
                            Os leads do formulário do anúncio entram direto no funil, na triagem, sem duplicar quem já
                            é contato pelo telefone. Funciona mesmo sem a conta conectada acima.
                        </p>
                    </div>

                    {leadForm?.key ? (
                        <>
                            <div className="grid grid-cols-1 gap-5">
                                <div className="form-control w-full">
                                    <label className="label py-1" htmlFor="google-lead-form-url">
                                        <span className="label-text text-sm font-medium">URL do webhook</span>
                                    </label>
                                    <div className="join w-full">
                                        <input
                                            id="google-lead-form-url"
                                            className="input input-bordered join-item w-full font-mono text-sm"
                                            value={leadForm.webhookUrl}
                                            readOnly
                                        />
                                        <button
                                            type="button"
                                            className="btn join-item"
                                            onClick={() => copyToClipboard(leadForm.webhookUrl, 'URL')}
                                            title="Copiar a URL"
                                        >
                                            <MdContentCopy className="text-lg" />
                                        </button>
                                    </div>
                                </div>
                                <div className="form-control w-full">
                                    <label className="label py-1" htmlFor="google-lead-form-key">
                                        <span className="label-text text-sm font-medium">Chave</span>
                                    </label>
                                    <div className="join w-full">
                                        <input
                                            id="google-lead-form-key"
                                            className="input input-bordered join-item w-full font-mono text-sm"
                                            value={leadForm.key}
                                            readOnly
                                        />
                                        <button
                                            type="button"
                                            className="btn join-item"
                                            onClick={() => copyToClipboard(leadForm.key!, 'Chave')}
                                            title="Copiar a chave"
                                        >
                                            <MdContentCopy className="text-lg" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-xl bg-base-200/50 p-4 text-sm text-base-content/70 space-y-1">
                                <p className="font-medium text-base-content">Como configurar no Google Ads</p>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Abra o formulário de lead do anúncio e vá até as opções de integração de leads.</li>
                                    <li>Escolha o webhook, cole a URL e a chave acima e salve.</li>
                                    <li>Clique em enviar dados de teste: o envio aparece logo abaixo.</li>
                                </ol>
                            </div>

                            <p className="text-sm text-base-content/70">
                                {leadForm.lastReceivedAt
                                    ? `Último envio recebido em ${new Date(leadForm.lastReceivedAt).toLocaleString('pt-BR', {
                                          dateStyle: 'short',
                                          timeStyle: 'short',
                                      })}${leadForm.lastWasTest ? ' (teste do Google)' : ''}.`
                                    : 'Nenhum envio recebido ainda.'}
                            </p>

                            <div className="flex items-center justify-between gap-2">
                                <button className="btn btn-ghost btn-sm gap-1" onClick={load}>
                                    <MdRefresh className="text-lg" /> Atualizar
                                </button>
                                <button className="btn btn-ghost btn-sm gap-1" onClick={() => setShowRotateKey(true)}>
                                    <MdVpnKey className="text-lg" /> Gerar nova chave
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex justify-end">
                            <button className="btn btn-secondary btn-sm gap-2" onClick={handleRotateKey} disabled={rotatingKey}>
                                {rotatingKey ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdVpnKey className="text-base" />
                                )}
                                Gerar chave do webhook
                            </button>
                        </div>
                    )}
                </div>
            )}

            {!loading && <StoreGoogleTagsCard />}

            <ConfirmModal
                isOpen={showRotateKey}
                onClose={() => setShowRotateKey(false)}
                onConfirm={handleRotateKey}
                title="Gerar nova chave"
                message="A chave atual deixa de ser aceita: os leads do formulário param de chegar até a nova chave ser colada no Google Ads. Deseja continuar?"
                confirmButtonText="Gerar nova chave"
                cancelButtonText="Cancelar"
                loading={rotatingKey}
                confirmButtonClass="btn-warning"
            />

            <ConfirmModal
                isOpen={showDisconnect}
                onClose={() => setShowDisconnect(false)}
                onConfirm={handleDisconnect}
                title="Desconectar Google Ads"
                message="A conta do Google Ads deixa de ser usada pela integração até reconectar. Deseja continuar?"
                confirmButtonText="Desconectar"
                cancelButtonText="Cancelar"
                loading={disconnecting}
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
